/**
 * The two things both inline nodes need, in one place.
 *
 * Planning/TME-WIKI-2026-09-02.md §7, §9.5, D23, R15.
 *
 * ⚠️ A TIPTAP NODE VIEW CANNOT READ REACT CONTEXT RELIABLY - it is rendered
 * through a portal by the editor, not by the component tree that owns the data.
 * So the shell publishes what the nodes need HERE, and the nodes read it. That
 * is deliberate and small: the only things published are DISPLAY text and a
 * navigator, so a stale value shows an old name and never a wrong link.
 */

/**
 * The Script Repository listing, published once per shell mount (H3, R25).
 *
 * D23: a script's name on a wiki page is resolved LIVE from this, and the
 * STORED `targetLabel` is the fallback for a script that no longer exists.
 * Revision 2 of the plan had AdminScriptsFunction refresh the stored label on
 * rename, which needed a grant §13.2 refuses and was the exact shape Bill
 * rejected for task renames.
 */
let scriptDirectory = new Map();

export function setScriptDirectory(scripts) {
    scriptDirectory = new Map((scripts || []).map(s => [s.scriptId, s]));
}

export function lookupScript(scriptId) {
    return scriptDirectory.get(scriptId) || null;
}

/** Test seam, so one suite's directory cannot satisfy another's. */
export function resetScriptDirectory() {
    scriptDirectory = new Map();
}

/**
 * The app's base path, computed the way `TaskDetailsModal.handleViewScript`
 * does.
 *
 * ⚠️ THE ROUTER IS MOUNTED `basename={process.env.PUBLIC_URL}` (App.js), which
 * on preview is `/gantt`. A stored `<a href="/admin/scripts/…">` clicked
 * NATIVELY leaves the SPA entirely and lands at the domain root, outside the app
 * prefix - the same class of defect as the login redirect fixed on 2026-08-21.
 * The stored markup keeps the plain absolute href, which is what makes it
 * degrade gracefully; only the rendered behaviour is intercepted.
 */
export function adminBasePath(pathname) {
    const path = pathname === undefined ? window.location.pathname : pathname;
    const adminIndex = path.indexOf('/admin/');
    return adminIndex > 0 ? path.substring(0, adminIndex) : '';
}

/**
 * The in-app navigator, published by the shell.
 *
 * With one, a page link is a route change and the editor's neighbours stay
 * mounted. Without one - a node rendered outside the portal, or a test - the
 * base-path form is used, which still reaches the right page.
 */
let navigator = null;

export function setWikiNavigator(fn) {
    navigator = typeof fn === 'function' ? fn : null;
}

export function resetWikiNavigator() {
    navigator = null;
}

export function navigateWithinAdmin(appPath) {
    if (navigator) {
        navigator(appPath);
        return;
    }
    window.location.assign(`${adminBasePath()}${appPath}`);
}
